import { gradeCompact } from '../../../lib/gradeLabel';
import { timeOf } from '../../../lib/dates';
export const pct = (v: number | null | undefined, d = 1) => (v == null || !Number.isFinite(v) ? '—' : `${v.toFixed(d)}%`);
export const signed = (v: number | null | undefined) => (v == null || !Number.isFinite(v) ? '—' : `${v > 0 ? '+' : v < 0 ? '−' : ''}${Math.abs(v).toFixed(1)}`);
export function deltaChip(v: number | null | undefined): { tone: 'up' | 'down' | 'neutral'; text: string } {
  if (v == null || !Number.isFinite(v)) return { tone: 'neutral', text: '—' };
  if (v === 0) return { tone: 'neutral', text: '0.0' };
  return { tone: v > 0 ? 'up' : 'down', text: `${v > 0 ? '↑' : '↓'} ${Math.abs(v).toFixed(1)}` };
}
/** Whole-number delta chip: "↑ 3 pts", "↓ 4%". Null (no data to compare) gives no chip. */
export function deltaChipWhole(v: number | null | undefined, suffix: string): { tone: 'up' | 'down' | 'neutral'; text: string } | null {
  if (v == null || !Number.isFinite(v)) return null;
  const r = Math.round(v);
  if (r === 0) return { tone: 'neutral', text: `0${suffix}` };
  return { tone: r > 0 ? 'up' : 'down', text: `${r > 0 ? '↑' : '↓'} ${Math.abs(r)}${suffix}` };
}
export function ago(iso: string, now = new Date()): string {
  const days = Math.floor((now.getTime() - new Date(iso).getTime()) / 86_400_000);
  return days <= 0 ? 'Today' : days === 1 ? '1 day' : `${days} days`;
}
/** Relative "last seen" label: "Just now", "5 min ago", "Today", "3 days ago", or "Never" when null. */
export function lastSeen(iso: string | null | undefined, now = new Date()): string {
  if (!iso) return 'Never';
  const ms = now.getTime() - new Date(iso).getTime();
  if (Number.isNaN(ms)) return 'Never';
  const mins = Math.floor(ms / 60_000);
  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins} min ago`;
  const days = Math.floor(ms / 86_400_000);
  if (days < 1) return 'Today';
  return days === 1 ? '1 day ago' : `${days} days ago`;
}
export function greeting(now = new Date()): string {
  const h = now.getHours();
  return h < 12 ? 'Good morning' : h < 17 ? 'Good afternoon' : 'Good evening';
}
/** "Monday, 12 October" (the design's comma after the weekday). "Term 1, week 6" has no data source and stays out. */
export function todayEyebrow(now = new Date()): string {
  const weekday = now.toLocaleDateString('en-GB', { weekday: 'long' });
  const rest = now.toLocaleDateString('en-GB', { day: 'numeric', month: 'long' });
  return `${weekday}, ${rest}`;
}
/** The panel's one compact grade label (lib/gradeLabel): "Grade 10" / "10" → "G10"; "Grade 12 Natural" → "G12 Nat". */
export const compactGrade = (name: string | null | undefined) => gradeCompact(name);
/** "G10 · B" (or "Grade 10 · B" when not compact) from a grade name and a section; either part may be missing. */
export function gradeSectionLabel(gradeName: string | null | undefined, section: { name?: string; display?: string } | null | undefined, compact = true): string {
  return [(compact ? compactGrade(gradeName) : gradeName) || null, section ? (section.name || section.display || null) : null].filter(Boolean).join(' · ');
}
/** Alerts list "when": "today, 8:10 AM", "yesterday", then "N days ago". */
export function whenLabel(iso: string, now = new Date()): string {
  const t = new Date(iso);
  const startOf = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const days = Math.round((startOf(now) - startOf(t)) / 86_400_000);
  if (days <= 0) return `today, ${timeOf(t)}`;
  return days === 1 ? 'yesterday' : `${days} days ago`;
}
const WORDS = ['No', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine', 'Ten'];
/** "Three" for small counts (design copy), digits above ten. */
export const countWord = (n: number) => (n >= 0 && n < WORDS.length ? WORDS[n] : String(n));
export const initials = (name: string) => {
  const p = name.trim().split(/\s+/).filter(Boolean);
  return p.length === 0 ? '?' : (p[0][0] + (p.length > 1 ? p[p.length - 1][0] : '')).toUpperCase();
};
